import React, { Component, ErrorInfo, ReactNode } from "react";
import SomethingWentWrongCard from "components/card/something-went-wrong/SomethingWentWrongCard";
import ApplicationContainer from "components/application-container/ApplicationContainer";

interface ErrorBoundaryProps {
    children: ReactNode;
    /** Custom fallback. Receives the caught error and a reset callback. */
    fallback?: (error: Error, reset: () => void) => ReactNode;
    /** Optional hook for external error reporting (e.g. monitoring service). */
    onError?: (error: Error, info: ErrorInfo) => void;
    /**
     * When this value changes, the boundary resets its error state.
     * Pass the route key or location.pathname to recover automatically on navigation.
     */
    resetKey?: unknown;
    /** Retry URL passed to the default fallback's recovery action. */
    retryUrl?: string;
}

interface ErrorBoundaryState {
    error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error("ErrorBoundary caught an error:", error, info);
        this.props.onError?.(error, info);
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps): void {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.reset();
        }
    }

    reset = (): void => {
        this.setState({ error: null });
    };

    render(): ReactNode {
        const { error } = this.state;
        if (!error) {
            return this.props.children;
        }

        if (this.props.fallback) {
            return this.props.fallback(error, this.reset);
        }

        return (
            <div data-testid="error-boundary-fallback" className="w-full">
                <ApplicationContainer>
                    <SomethingWentWrongCard retryUrl={this.props.retryUrl} />
                </ApplicationContainer>
            </div>
        );
    }
}

export default ErrorBoundary;
